import { Link } from 'react-router-dom';
import type { ActiveDeployment } from '../../api';
import { useActiveDeployments } from '../DeploymentToasts';
import { Icon } from '../icons';
import { formatClock, useNow } from '../runTiming';

/**
 * ProjectDeploymentBanner: a deployment that is running shows in the
 * project's header row, between the name and Deploy, on every section, so nobody has to open Deployments to
 * learn that a release is being built or switched. One line per running
 * deployment of this project: what, where, which stage, how far, and a link
 * to its page. It leaves the header when the deployment ends; the outcome is
 * the deployment history's and the toast's to tell.
 */
export function ProjectDeploymentBanner({ projectId, deploymentPath }: { projectId: string; deploymentPath: (deployment: ActiveDeployment) => string }) {
  const running = useActiveDeployments().filter((deployment) => deployment.project_id === projectId && !deployment.finished);
  const now = useNow(running.length > 0);
  if (running.length === 0) return null;
  return (
    <section className="project-deploying" aria-label="Deployments in progress" data-testid="project-deploying" aria-live="polite">
      <ul className="project-deploying__list">
        {running.map((deployment) => (
          <RunningDeployment key={deployment.deployment_id} deployment={deployment} now={now} to={deploymentPath(deployment)} />
        ))}
      </ul>
    </section>
  );
}

function RunningDeployment({ deployment, now, to }: { deployment: ActiveDeployment; now: number; to: string }) {
  const p = deployment.progress;
  const started = deployment.started_at ? Date.parse(deployment.started_at) : null;
  const name = deployment.component_name || 'Deployment';
  const where =
    p.current_stage_title && p.stage_total
      ? `Stage ${p.current_stage_index}/${p.stage_total} · ${p.current_stage_title}`
      : p.current_step_title
        ? `Step ${p.current_step_index}/${p.total} · ${p.current_step_title}`
        : 'Starting';
  const verb = deployment.rehearsal ? 'Rehearsing' : 'Deploying';
  return (
    <li className="project-deploying__item" data-testid={`project-deploying-${deployment.deployment_id}`}>
      <Icon name="refresh" size={16} className="co-spin-slow project-deploying__icon" />
      <span className="project-deploying__what">
        <strong>
          {verb} {name}
        </strong>
        <span className="project-deploying__facts">
          {deployment.environment_name}
          {deployment.commit_sha ? (
            <>
              {' · '}
              <code translate="no">{deployment.commit_sha.slice(0, 7)}</code>
            </>
          ) : null}
        </span>
      </span>
      <span className="project-deploying__progress">
        <span className="project-deploying__where">{where}</span>
        <span
          className="project-deploying__bar"
          role="progressbar"
          aria-label={`${verb} ${name}`}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={p.percent}
          aria-valuetext={`${p.percent}%, ${p.done} of ${p.total} steps done`}
        >
          <span className="project-deploying__fill" style={{ transform: `scaleX(${p.percent / 100})` }} />
        </span>
        <span className="project-deploying__meta tabular-nums">
          {p.percent}%{started !== null ? ` · ${formatClock(now - started)}` : ''}
        </span>
      </span>
      <Link className="button button--small" to={to}>
        View <span className="visually-hidden">the {name} deployment</span>
      </Link>
    </li>
  );
}
